import { useEffect, useState } from "react";
import TextColorFragment from "../../../../components/fragment/text-color/TextColorFragment";
import { RazorRadioCheck } from "../../../../components/razor-reuse";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../../redux/type";
import { getActivityChart } from "../../../../redux/report";
import NoContentPage from "../../../../layout/dashboard/no-content/NoContentPage";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface dataSetProp {
  label: string;
  promoter: number;
  detractors: number;
  passives: number;
}

export default function AverageActivityChart() {
  const dispatch = useDispatch();
  const [filterFeedback, setFilterFeedback] = useState("all");
  const [filterActivity, setFilterActivity] = useState("monthly");

  const { activity_chart, loading_activity } = useSelector(
    (state: RootState) => state.report
  ); // for report

  useEffect(() => {
    const fetchData = async () => {
      const payload = { filter: filterActivity };
      dispatch(getActivityChart(payload) as any);
    };

    fetchData();
  }, [filterActivity, dispatch]);

  const actualDataSet = (activity_chart[filterActivity] as dataSetProp[]) || [];

  const initialDataSet = [
    {
      label: "Promoters",
      data: actualDataSet.map((item) => item.promoter),
      backgroundColor: "#009933",
      borderRadius: 4,
    },
    {
      label: "Passives",
      data: actualDataSet.map((item) => item.passives),
      backgroundColor: "#EFBF04",
      borderRadius: 4,
    },
    {
      label: "Detractors",
      data: actualDataSet.map((item) => item.detractors),
      backgroundColor: "#F76659",
      borderRadius: 4,
    },
  ];

  // filter base on filterFeedback
  const getFilteredDatasets = () => {
    switch (filterFeedback) {
      case "promoters":
        return [initialDataSet[0]];
      case "passives":
        return [initialDataSet[1]];
      case "detractors":
        return [initialDataSet[2]];
      default:
        return initialDataSet;
    }
  };

  // Grouping data by label to get chart data
  //   This is the final result
  const chartData = {
    labels: actualDataSet.map((item) => item.label),
    datasets: getFilteredDatasets(),
  };

  return (
    <div className="min-h-[480px] rounded-[8px] bg-white p-[16px] px-[24px] lg:col-span-8 grid">
      <div className="inline-flex flex-col">
        <h6 className="font-medium text-[14px] text-[#162238] mb-[15px] h-fit">
          Average Activity Chart
        </h6>
        {/* tabs */}
        <ul className="bg-[#D9DBDC1A] rounded-[8px] px-[12px] py-[6px] flex justify-between items-center [&_li]:rounded-[8px] [&_li]:cursor-pointer [&_li]:h-[40px] [&_li]:w-full [&_li]:max-w-[127px] [&_li]:grid [&_li]:place-content-center">
          <li
            className={`font-medium text-[14px] ${
              filterFeedback === "all"
                ? "bg-white text-primary"
                : "text-[#758089] "
            }`}
            onClick={() => setFilterFeedback("all")}
          >
            All feedback
          </li>
          <li
            className={`font-medium text-[14px] ${
              filterFeedback === "promoters"
                ? "bg-white text-primary"
                : "text-[#758089] "
            }`}
            onClick={() => setFilterFeedback("promoters")}
          >
            Promoters
          </li>
          <li
            className={`font-medium text-[14px] ${
              filterFeedback === "passives"
                ? "bg-white text-primary"
                : "text-[#758089] "
            }`}
            onClick={() => setFilterFeedback("passives")}
          >
            Passives
          </li>
          <li
            className={`font-medium text-[14px] ${
              filterFeedback === "detractors"
                ? "bg-white text-primary"
                : "text-[#758089] "
            }`}
            onClick={() => setFilterFeedback("detractors")}
          >
            Detractors
          </li>
        </ul>

        <section className="flex justify-between items-center mt-[20px]">
          <p className="text-[#758089] font-medium text-[12px]">
            Promoter{" "}
            <TextColorFragment color="rgba(0, 153, 51, 1)" text={"70%"} />
          </p>
          <div className="flex gap-[16px] xl:gap-[20px] mb-[15px]">
            <label
              htmlFor="weekly"
              className="inline-flex items-center gap-[4px] text-[10px] font-medium text-[#596080]"
            >
              <RazorRadioCheck
                color="purple-dark"
                key="weekly"
                checked={filterActivity === "weekly"}
                onChange={() => setFilterActivity("weekly")}
                id={1010}
                className={`radio[&_label]:!border-[0.15rem] [&_label]:!border-primary  ${
                  filterActivity === "weekly" && "[&_span]:!bg-primary"
                }`}
                name="weekly"
              />
              Weekly Activity
            </label>
            <label
              htmlFor="monthly"
              className="inline-flex items-center gap-[4px] text-[10px] font-medium text-[#596080]"
            >
              <RazorRadioCheck
                color="purple-dark"
                key="monthly"
                checked={filterActivity === "monthly"}
                onChange={() => setFilterActivity("monthly")}
                id={2020}
                className={`radio[&_label]:!border-[0.15rem] [&_label]:!border-primary  ${
                  filterActivity === "monthly" && "[&_span]:!bg-primary"
                }`}
                name="monthly"
              />
              Monthly Activity
            </label>
            <label
              htmlFor="quarterly"
              className="inline-flex items-center gap-[4px] text-[10px] font-medium text-[#596080]"
            >
              <RazorRadioCheck
                color="purple-dark"
                key="quarterly"
                checked={filterActivity === "quarterly"}
                onChange={() => setFilterActivity("quarterly")}
                id={3030}
                className={`radio[&_label]:!border-[0.15rem] [&_label]:!border-primary  ${
                  filterActivity === "quarterly" && "[&_span]:!bg-primary"
                }`}
                name="quarterly"
              />
              Quarterly Activity
            </label>
            <label
              htmlFor="yearly"
              className="inline-flex items-center gap-[4px] text-[10px] font-medium text-[#596080]"
            >
              <RazorRadioCheck
                color="purple-dark"
                key="yearly"
                checked={filterActivity === "yearly"}
                onChange={() => setFilterActivity("yearly")}
                id={4040}
                className={`radio[&_label]:!border-[0.15rem] [&_label]:!border-primary  ${
                  filterActivity === "yearly" && "[&_span]:!bg-primary"
                }`}
                name="yearly"
              />
              Yearly Activity
            </label>
          </div>
        </section>

        <section className="flex-1 pt-[6px] w-full ">
          {loading_activity ? (
            <NoContentPage height="35rem" loading={true} />
          ) : (
            <Bar data={chartData} options={options} />
          )}
        </section>
      </div>
    </div>
  );
}

// const data = {
//   labels: [
//     "Jan",
//     "Feb",
//     "Mar",
//     "Apr",
//     "May",
//     "Jun",
//     "Jul",
//     "Aug",
//     "Sept",
//     "Oct",
//     "Nov",
//     "Dec",
//   ],
//   datasets: [
//     {
//       label: "Promoter",
//       data: [350, 580, 350, 450, 500, 350, 550, 460, 350, 510, 350, 350],
//       backgroundColor: "#009933",
//       borderRadius: 4,
//     },
//     {
//       label: "Passives",
//       data: [260, 260, 480, 420, 410, 330, 260, 260, 260, 370, 260, 460],
//       backgroundColor: "#EFBF04",
//       borderRadius: 4,
//     },
//     {
//       label: "Detractors",
//       data: [305, 110, 185, 305, 190, 305, 50, 305, 305, 130, 305, 305],
//       backgroundColor: "#F76659",
//       borderRadius: 4,
//     },
//   ],
// };

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false, // Hides the legend
    },
    datalabels: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false, // Hides x-axis grid lines
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        display: false, // Hides y-axis grid lines
      },
    },
  },
};

const actualDataSet = [];

const dummyDataMonthly = [
  {
    label: "Jan",
    promoter: 23,
    detractors: 11,
    passives: 9,
  },
  {
    label: "Feb",
    promoter: 39,
    detractors: 8,
    passives: 10,
  },
  {
    label: "Mar",
    promoter: 18,
    detractors: 20,
    passives: 7,
  },
  {
    label: "Apr",
    promoter: 19,
    detractors: 11,
    passives: 13,
  },
  {
    label: "May",
    promoter: 8,
    detractors: 22,
    passives: 10,
  },
  {
    label: "Jun",
    promoter: 29,
    detractors: 7,
    passives: 4,
  },
];

const dummyDataWeekly = [
  {
    label: "Week 1",
    promoter: 11,
    detractors: 23,
    passives: 9,
  },
  {
    label: "Week 2",
    promoter: 39,
    detractors: 8,
    passives: 10,
  },
  {
    label: "Week 3",
    promoter: 13,
    detractors: 32,
    passives: 7,
  },
  {
    label: "Week 4",
    promoter: 19,
    detractors: 11,
    passives: 13,
  },
  {
    label: "Week 5",
    promoter: 8,
    detractors: 22,
    passives: 10,
  },
];

const dummyDataQuarterly = [
  {
    label: "Q1",
    promoter: 11,
    detractors: 23,
    passives: 9,
  },
  {
    label: "Q2",
    promoter: 39,
    detractors: 8,
    passives: 10,
  },
  {
    label: "Q3",
    promoter: 13,
    detractors: 32,
    passives: 7,
  },
  {
    label: "Q4",
    promoter: 19,
    detractors: 11,
    passives: 13,
  },
];
